'use client';

import { Box, Grid, HStack, VStack } from '@terra/ui-styled-system';
import { Button, Text, TextDiffView } from '@terra/ui-design-system';
import { useRoleEditStore } from '../../store/roleEditStore';
import type {
  CustomRoleEditableField,
  PendingCustomRoleFieldEdit,
} from '../../store/roleEditStore';

const ROLE_FIELD_LABELS: Record<CustomRoleEditableField, string> = {
  name: 'Name',
  description: 'Description',
};

type RoleFieldPendingEditProps = {
  pendingEdit: PendingCustomRoleFieldEdit;
  onAccept: () => void;
  onDiscard: () => void;
};

function RoleFieldPendingEdit({
  pendingEdit,
  onAccept,
  onDiscard,
}: RoleFieldPendingEditProps) {
  const fieldLabel = ROLE_FIELD_LABELS[pendingEdit.field];

  return (
    <VStack
      gap={3}
      alignItems="stretch"
      mb={2}
      p={4}
      borderRadius="lg"
      borderWidth="1px"
      borderColor="border.info"
      bg="bg.canvas"
    >
      <HStack justifyContent="space-between" alignItems="center" gap={2}>
        <Text fontSize="sm" fontWeight="semibold" color="fg.default">
          {fieldLabel}
        </Text>
        <HStack gap={1}>
          <Button size="xs" variant="ghost" onClick={onDiscard} type="button">
            Discard
          </Button>
          <Button size="xs" variant="solid" onClick={onAccept} type="button">
            Accept
          </Button>
        </HStack>
      </HStack>
      <Grid columns={2} gap={4}>
        <VStack gap={1} alignItems="stretch">
          <Text fontSize="xs" color="fg.muted">
            Before
          </Text>
          <Box
            p={3}
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.subtle"
            bg="bg.subtle"
            minH="80px"
          >
            <Text fontSize="sm" whiteSpace="pre-wrap">
              {pendingEdit.before || '—'}
            </Text>
          </Box>
        </VStack>
        <VStack gap={1} alignItems="stretch">
          <Text fontSize="xs" color="fg.muted">
            After
          </Text>
          <Box
            p={3}
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.info"
            bg="bg.canvas"
            minH="80px"
          >
            <TextDiffView
              before={pendingEdit.before}
              after={pendingEdit.after}
            />
          </Box>
        </VStack>
      </Grid>
    </VStack>
  );
}

type RoleCopilotPendingEditsSectionProps = {
  roleId: string;
  roleLabel?: string;
  onAcceptField: (field: CustomRoleEditableField) => void;
  onDiscardField: (field: CustomRoleEditableField) => void;
  onAcceptAll: () => void;
  onDiscardAll: () => void;
};

export function RoleCopilotPendingEditsSection({
  roleId,
  roleLabel,
  onAcceptField,
  onDiscardField,
  onAcceptAll,
  onDiscardAll,
}: RoleCopilotPendingEditsSectionProps) {
  const pendingAiEdits = useRoleEditStore(
    (state) => state.pendingAiEdits[roleId],
  );

  if (!pendingAiEdits?.fields.length) {
    return null;
  }

  return (
    <Box
      mb={4}
      p={4}
      borderRadius="lg"
      borderWidth="1px"
      borderColor="border.info"
      bg="bg.subtle"
    >
      <VStack gap={3} alignItems="stretch">
        <HStack justifyContent="space-between" alignItems="center" gap={2}>
          <VStack gap={1} alignItems="flex-start">
            <Text fontSize="sm" fontWeight="semibold">
              {pendingAiEdits.fields.length} AI-proposed change
              {pendingAiEdits.fields.length === 1 ? '' : 's'} pending review
              {roleLabel ? ` (${roleLabel})` : ''}
            </Text>
            {pendingAiEdits.reason ? (
              <Text fontSize="xs" color="fg.muted" fontStyle="italic">
                {pendingAiEdits.reason}
              </Text>
            ) : null}
          </VStack>
          <HStack gap={2}>
            <Button size="xs" variant="outline" onClick={onDiscardAll}>
              Discard all
            </Button>
            <Button size="xs" variant="solid" onClick={onAcceptAll}>
              Accept all
            </Button>
          </HStack>
        </HStack>
        {pendingAiEdits.fields.map((entry) => (
          <RoleFieldPendingEdit
            key={entry.field}
            pendingEdit={entry}
            onAccept={() => onAcceptField(entry.field)}
            onDiscard={() => onDiscardField(entry.field)}
          />
        ))}
      </VStack>
    </Box>
  );
}
